import { useQuery } from '@apollo/client';
import { inflect } from 'inflection';
import _ from 'lodash';
import React from 'react';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import { Cards } from '../../components/Card';
import LoadingIndicator from '../../components/LoadingIndicator';
import { gql } from '../../graphql/generated/gql';
import { ProvisionType } from '../../graphql/generated/graphql';
import keyIcon from '../../icons/non-prefilled/16x16/key.svg';
import SettingCard from '../../settings/SettingCard';
import SettingsContainerView from '../../settings/SettingsContainerView';
import { userBadgeColors } from '../../user/profile/UserBadge';
import * as styles from './AdminProvisionsView.module.scss';
import ProvisionSSODefaultBadge from './ProvisionSSODefaultBadge';

gql(`
  fragment AdminProvisionsView_provisions on Provision {
    id
    name
    favoritePosts {
      id
    }
    favoriteTopics {
      id
    }
    topics {
      id
    }
    type
    isSsoDefault
  }
`);

const ADMIN_PROVISIONS_VIEW_QUERY = gql(`
  query AdminProvisionsView {
    session: currentSession {
      organization {
        id
        provisions {
          ...AdminProvisionsView_provisions
        }
      }
    }
  }
`);

const AdminProvisionsView = () => {
  const { data, loading } = useQuery(ADMIN_PROVISIONS_VIEW_QUERY);
  const provisions = data?.session?.organization?.provisions ?? [];

  return (
    <SettingsContainerView
      title="Provisions"
      subtitle="Provisions allow you to onboard team members with content that’s useful to them. Create groupings and assign them to users when they're invited. "
    >
      {loading ? (
        <LoadingIndicator />
      ) : (
        <>
          <Cards>
            {provisions.map((p) => {
              if (!p) {
                return null;
              }
              const details = [];
              const favoritesCount =
                (p.favoritePosts?.length ?? 0) +
                (p.favoriteTopics?.length ?? 0);

              if (p.topics?.length && p.topics.length > 0) {
                details.push(
                  `${p.topics.length} ${inflect(
                    'topic memberships',
                    p.topics.length,
                  )}`,
                );
              }

              if (favoritesCount > 0) {
                details.push(
                  `${favoritesCount} ${inflect('favorites', favoritesCount)}`,
                );
              }

              return (
                <SettingCard
                  testId={`provisionSettingCard-${p.id}`}
                  actions={
                    <Button
                      to={`/admin/provisions/${p.id}`}
                      variant="secondary"
                    >
                      Edit
                    </Button>
                  }
                  key={p.id}
                  description={
                    <>
                      {p.name}
                      <div className={styles.badgeWrapper}>
                        <Badge
                          color={
                            userBadgeColors[
                              p.type as keyof typeof userBadgeColors
                            ]
                          }
                          icon={
                            p.type === ProvisionType.Admin ? (
                              <Badge.Icon>{keyIcon}</Badge.Icon>
                            ) : null
                          }
                        >
                          {_.capitalize(p.type)}
                        </Badge>
                        {p.isSsoDefault && <ProvisionSSODefaultBadge />}
                      </div>
                    </>
                  }
                  details={details.join(', ')}
                />
              );
            })}
          </Cards>
          <div className={styles.addNewWrapper}>
            <Button to="/admin/provisions/new" variant="primary">
              Add new
            </Button>
          </div>
        </>
      )}
    </SettingsContainerView>
  );
};

export default AdminProvisionsView;
